import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useCart } from '../lib/cart';
import { scrollToTarget } from '../lib/scroll';
import { BagIcon, CloseIcon, MenuIcon, SearchIcon } from './icons';
import { Lotus } from './lotus';

const NAV = [
  { to: '/sarees', label: 'All sarees' },
  { to: '/sarees?featured=true', label: 'Featured' },
  { to: '/#weaves', label: 'Weaves' },
];

const navClass = 'text-[0.78rem] font-medium tracking-[0.18em] text-champagne/90 uppercase transition-colors hover:text-gold';

export function SiteHeader() {
  const { count, open } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  // A section link on the page we're already on glides there directly, without waiting for a navigation.
  const onNavClick = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    const [path, hash] = to.split('#');
    if (!hash || path !== location.pathname) return;
    const target = document.getElementById(hash);
    if (!target) return;
    e.preventDefault();
    setMenuOpen(false);
    scrollToTarget(target);
  };

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  return (
    <header data-site-header className="on-maroon fixed inset-x-0 top-0 z-30 border-b border-gold/50 bg-maroon text-champagne">
      <div className="mx-auto grid h-16 max-w-7xl grid-cols-[1fr_auto_1fr] items-center px-4 sm:h-20 sm:px-8">
        <div className="flex items-center gap-8">
          <button
            type="button"
            className="-ml-2 grid size-11 place-items-center lg:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
          <nav aria-label="Main" className="hidden items-center gap-8 lg:flex">
            {NAV.map((item) => (
              <NavLink key={item.label} to={item.to} className={navClass} end onClick={onNavClick(item.to)}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <Link to="/" className="group flex flex-col items-center gap-1 px-2 text-center" aria-label="House of Neyara - home">
          <Lotus className="hidden h-3.5 w-6 text-gold transition-transform duration-500 group-hover:-translate-y-0.5 sm:block" />
          <span className="font-display text-[0.95rem] font-medium tracking-[0.3em] whitespace-nowrap text-champagne sm:text-[1.3rem] sm:tracking-[0.34em]">
            House of Neyara
          </span>
        </Link>

        <div className="flex items-center justify-end gap-1 sm:gap-3">
          <button
            type="button"
            className="grid size-11 cursor-pointer place-items-center text-champagne/90 transition-colors hover:text-gold"
            aria-label="Search sarees"
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((v) => !v)}
          >
            <SearchIcon />
          </button>
          <button
            type="button"
            onClick={open}
            className="relative -mr-2 grid size-11 cursor-pointer place-items-center text-champagne/90 transition-colors hover:text-gold"
            aria-label={`Open bag, ${count} ${count === 1 ? 'item' : 'items'}`}
          >
            <BagIcon />
            {count > 0 && (
              <span
                key={count}
                className="tabular absolute top-1 right-0.5 grid size-5 animate-[bag-tick_.5s_var(--ease-out-expo)] place-items-center rounded-full bg-gold text-[0.68rem] font-semibold text-maroon-deep"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {searchOpen && <SearchBar onDone={() => setSearchOpen(false)} />}

      {menuOpen && (
        <nav id="mobile-nav" aria-label="Main" className="border-t border-gold/30 bg-maroon-deep px-4 pt-2 pb-6 lg:hidden">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={onNavClick(item.to)}
              className="flex min-h-12 items-center border-b border-gold/15 font-display text-sm tracking-[0.2em] uppercase"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

function SearchBar({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get('q')?.toString().trim();
    navigate(q ? `/sarees?q=${encodeURIComponent(q)}` : '/sarees');
    onDone();
  };

  return (
    <form role="search" onSubmit={submit} className="border-t border-gold/30 bg-maroon-deep">
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:px-8">
        <label htmlFor="site-search" className="sr-only">
          Search sarees
        </label>
        <SearchIcon className="shrink-0 text-gold" />
        <input
          ref={input}
          id="site-search"
          name="q"
          type="search"
          placeholder="Search by name, weave, fabric or colour"
          className="min-h-11 w-full bg-transparent font-script text-xl text-champagne italic placeholder:text-champagne/55 focus:outline-none"
          onKeyDown={(e) => e.key === 'Escape' && onDone()}
        />
        <button type="submit" className="btn btn-gold min-h-10 px-5">
          Search
        </button>
      </div>
    </form>
  );
}
